import axios from "axios";
import { NGROK } from "../../../../APIs";
import {
    EventFilter,
    FindingFilter,
    FindingStatus,
    GitlabSetupInfo,
    IdentityFilter,
    ResourceFilter,
    SaasActivity,
    SaasAppRegistration,
    SaasConnection,
    SaasConnectResult,
    SaasConnectUrl,
    SaasEvent,
    SaasFinding,
    SaasFindingSummary,
    SaasIdentity,
    SaasIdentityDetail,
    SaasOverview,
    SaasPage,
    SaasProvider,
    SaasResource,
    SaasResourceDetail,
    SaasScopeOption,
    SaasSyncRequest,
    TimeRange,
} from "./saasTypes";

type Params = Record<string, string | number | boolean | undefined | null>;

const base = (provider: SaasProvider) => `${NGROK}/api/saas/${provider}`;

const clean = (params: Params) =>
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));

const get = async <T>(url: string, params: Params): Promise<T> => (await axios.get<T>(url, { params: clean(params) })).data;

export const launchConnect = (target: SaasConnectUrl) => {
    if (target.method !== "POST") {
        window.location.href = target.url;
        return;
    }
    const form = document.createElement("form");
    form.method = "POST";
    form.action = target.url;
    Object.entries(target.formFields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
};

export const saasErrorMessage = (error: unknown, fallback: string): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        if (data?.message) return data.message;
    }
    return fallback;
};

export const saasApi = {
    connections: (provider: SaasProvider, tenantName: string) =>
        get<SaasConnection[]>(`${base(provider)}/connections`, { tenantName }),

    syncConnection: async (provider: SaasProvider, tenantName: string, connectionId: number) =>
        (await axios.post<SaasSyncRequest>(`${base(provider)}/connections/${connectionId}/sync`, null, { params: { tenantName } })).data,

    disconnect: async (provider: SaasProvider, tenantName: string, connectionId: number) =>
        (await axios.delete<{ message: string }>(`${base(provider)}/connections/${connectionId}`, { params: { tenantName } })).data,

    connectUrl: (provider: SaasProvider, tenantName: string, instanceUrl?: string, organization?: string) =>
        get<SaasConnectUrl>(`${base(provider)}/auth/connect-url`, { tenantName, instanceUrl, organization }),

    gitlabSetupInfo: (tenantName: string, instanceUrl?: string) =>
        get<GitlabSetupInfo>(`${base("gitlab")}/auth/setup-info`, { tenantName, instanceUrl }),

    connectGitlabToken: async (tenantName: string, instanceUrl: string, accessToken: string) =>
        (await axios.post<SaasConnectResult>(`${base("gitlab")}/auth/token`, { instanceUrl, accessToken }, { params: { tenantName } })).data,

    appRegistrations: (provider: SaasProvider, tenantName: string) =>
        get<SaasAppRegistration[]>(`${base(provider)}/auth/app-registrations`, { tenantName }),

    saveAppRegistration: async (provider: SaasProvider, tenantName: string, payload: Record<string, string | undefined>) =>
        (await axios.post<SaasAppRegistration>(`${base(provider)}/auth/app-registrations`, payload, { params: { tenantName } })).data,

    deleteAppRegistration: async (provider: SaasProvider, tenantName: string, registrationId: number) =>
        (await axios.delete(`${base(provider)}/auth/app-registrations/${registrationId}`, { params: { tenantName } })).data,

    gitlabGroups: (tenantName: string, connectionId: number) =>
        get<SaasScopeOption[]>(`${base("gitlab")}/connections/${connectionId}/groups`, { tenantName }),

    updateGitlabScope: async (tenantName: string, connectionId: number, groupIds: string[]) =>
        (await axios.put<SaasConnection>(`${base("gitlab")}/connections/${connectionId}/scope`, { groupIds }, { params: { tenantName } })).data,

    overview: (provider: SaasProvider, tenantName: string, connectionId: number | undefined, range: TimeRange) =>
        get<SaasOverview>(`${base(provider)}/analytics/overview`, { tenantName, connectionId, ...range }),

    activity: (provider: SaasProvider, tenantName: string, connectionId: number | undefined, range: TimeRange) =>
        get<SaasActivity>(`${base(provider)}/analytics/activity`, { tenantName, connectionId, ...range }),

    identities: (provider: SaasProvider, tenantName: string, connectionId: number | undefined, range: TimeRange,
                 filter: IdentityFilter, page: number, size: number) =>
        get<SaasPage<SaasIdentity>>(`${base(provider)}/analytics/identities`, { tenantName, connectionId, ...range, ...filter, page, size }),

    identityDetail: (provider: SaasProvider, tenantName: string, connectionId: number, login: string, range: TimeRange) =>
        get<SaasIdentityDetail>(`${base(provider)}/analytics/identities/detail`, { tenantName, connectionId, login, ...range }),

    resources: (provider: SaasProvider, tenantName: string, connectionId: number | undefined, range: TimeRange,
                filter: ResourceFilter, page: number, size: number) =>
        get<SaasPage<SaasResource>>(`${base(provider)}/analytics/resources`, {
            tenantName, connectionId, ...range, ...filter, sort: filter.sort === "default" ? undefined : filter.sort, page, size,
        }),

    resourceDetail: (provider: SaasProvider, tenantName: string, connectionId: number, resourceId: string, range: TimeRange) =>
        get<SaasResourceDetail>(`${base(provider)}/analytics/resources/detail`, { tenantName, connectionId, resourceId, ...range }),

    events: (provider: SaasProvider, tenantName: string, connectionId: number | undefined, range: TimeRange,
             filter: EventFilter, page: number, size: number) =>
        get<SaasPage<SaasEvent>>(`${base(provider)}/analytics/events`, { tenantName, connectionId, ...range, ...filter, page, size }),

    event: (provider: SaasProvider, tenantName: string, eventId: number) =>
        get<SaasEvent>(`${base(provider)}/analytics/events/${eventId}`, { tenantName }),

    findings: (provider: SaasProvider, tenantName: string, connectionId: number | undefined, filter: FindingFilter, page: number, size: number) =>
        get<SaasPage<SaasFinding>>(`${base(provider)}/analytics/findings`, { tenantName, connectionId, ...filter, page, size }),

    findingSummary: (provider: SaasProvider, tenantName: string, connectionId: number | undefined) =>
        get<SaasFindingSummary>(`${base(provider)}/analytics/findings/summary`, { tenantName, connectionId }),

    updateFinding: async (provider: SaasProvider, tenantName: string, findingId: number, status: FindingStatus) =>
        (await axios.put<SaasFinding>(`${base(provider)}/analytics/findings/${findingId}/status`, { status }, { params: { tenantName } })).data,
};
